import { useEffect, useState } from "react";
import { api, brl, API } from "../../lib/api";
import { toast } from "sonner";
import {
  Banknote, Smartphone, CreditCard, Copy, FileText, MessageCircle,
  CheckCircle2, Loader2, ArrowLeft, Lock, AlertTriangle, RefreshCw, Clock,
} from "lucide-react";

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#F5B301] placeholder:text-slate-400";

const optCls = (active) =>
  `py-3.5 px-3 rounded-xl border-2 text-sm font-semibold active:scale-95 transition-all flex items-center justify-center gap-2 ${
    active ? "border-[#F5B301] bg-gold-light text-navy" : "border-slate-200 bg-white text-slate-600"
  }`;

function ValueRows({ calc }) {
  if (!calc) return null;
  const cardSaldo = calc.charge !== calc.saldo;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-2" data-testid="payment-values">
      <div className="flex justify-between text-sm">
        <span className="text-slate-500">Valor total do serviço</span>
        <span className="font-semibold text-navy" data-testid="pay-total">{brl(calc.total)}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-slate-500">Sinal para reserva (via Pix)</span>
        <span className="font-semibold text-navy" data-testid="pay-sinal">{brl(calc.sinal)}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-slate-500">Saldo restante</span>
        <span className="font-semibold text-navy" data-testid="pay-saldo">{brl(calc.saldo)}</span>
      </div>
      {cardSaldo && (
        <div className="border-t border-slate-100 pt-2 flex justify-between text-sm">
          <span className="text-slate-500">Total do saldo no cartão</span>
          <span className="font-heading font-bold text-navy" data-testid="pay-charge">{brl(calc.charge)}</span>
        </div>
      )}
      {cardSaldo && calc.parcelas > 1 && (
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Parcelamento do saldo</span>
          <span className="font-semibold text-navy" data-testid="pay-parcelas">
            {calc.parcelas}x de {brl(calc.parcela_value)}
          </span>
        </div>
      )}
    </div>
  );
}

export function PaymentStep({ result, payment, setPayment, onBack, onNext }) {
  const [loading, setLoading] = useState(false);
  const eff = payment.forma === "cartao" ? payment.cardTipo : payment.forma || "dinheiro";

  useEffect(() => {
    const needsBrand = eff === "credito" || eff === "debito";
    if ((payment.forma === "cartao" && !payment.cardTipo) || (needsBrand && !payment.bandeira)) {
      setPayment((p) => ({ ...p, calc: null }));
      return;
    }
    setLoading(true);
    api
      .post("/payment/calculate", {
        quote_id: result.quote_id,
        forma: payment.forma === "cartao" ? payment.cardTipo : payment.forma || "dinheiro",
        bandeira: needsBrand ? payment.bandeira : null,
        parcelas: eff === "credito" ? payment.parcelas : null,
      })
      .then((r) => setPayment((p) => ({ ...p, calc: r.data })))
      .catch((e) => {
        setPayment((p) => ({ ...p, calc: null }));
        toast.error(e.response?.data?.detail || "Não foi possível calcular o pagamento");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment.forma, payment.cardTipo, payment.bandeira, payment.parcelas]);

  const canContinue =
    payment.forma &&
    payment.calc &&
    payment.cpfCnpj.replace(/\D/g, "").length >= 11 &&
    (payment.forma !== "cartao" || (payment.cardTipo && payment.bandeira));

  return (
    <div className="space-y-5" data-testid="payment-step">
      <h2 className="font-heading text-2xl font-bold text-navy">Pagamento</h2>

      <div className="flex gap-2.5 bg-gold-light border border-[#F5B301]/60 rounded-2xl p-3.5" data-testid="sinal-pix-aviso">
        <Smartphone className="w-5 h-5 text-navy shrink-0" />
        <p className="text-xs text-navy leading-relaxed font-medium">
          O sinal de reserva (30%) é pago exclusivamente via Pix — sem taxas e sem parcelamento. O saldo restante você
          escolhe como pagar abaixo.
        </p>
      </div>

      {loading && (
        <div className="flex justify-center py-2">
          <Loader2 className="w-6 h-6 animate-spin text-[#F5B301]" />
        </div>
      )}
      <ValueRows calc={payment.calc} />

      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
          Como deseja pagar o saldo restante?
        </p>
        <div className="grid grid-cols-3 gap-2.5">
          <button data-testid="forma-dinheiro" onClick={() => setPayment((p) => ({ ...p, forma: "dinheiro", calc: null }))} className={optCls(payment.forma === "dinheiro")}>
            <Banknote className="w-5 h-5" /> DINHEIRO
          </button>
          <button data-testid="forma-pix" onClick={() => setPayment((p) => ({ ...p, forma: "pix", calc: null }))} className={optCls(payment.forma === "pix")}>
            <Smartphone className="w-5 h-5" /> PIX
          </button>
          <button data-testid="forma-cartao" onClick={() => setPayment((p) => ({ ...p, forma: "cartao", calc: null }))} className={optCls(payment.forma === "cartao")}>
            <CreditCard className="w-5 h-5" /> CARTÃO
          </button>
        </div>
        {payment.forma === "dinheiro" && (
          <p className="text-[11px] text-slate-500 mt-2 leading-relaxed" data-testid="dinheiro-aviso">
            O saldo poderá ser pago em dinheiro no dia do serviço, conforme as condições contratadas. O sinal de reserva
            é exclusivamente via Pix.
          </p>
        )}
      </div>

      {payment.forma === "cartao" && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2.5">
            <button data-testid="card-credito" onClick={() => setPayment((p) => ({ ...p, cardTipo: "credito" }))} className={optCls(payment.cardTipo === "credito")}>
              CRÉDITO
            </button>
            <button data-testid="card-debito" onClick={() => setPayment((p) => ({ ...p, cardTipo: "debito" }))} className={optCls(payment.cardTipo === "debito")}>
              DÉBITO
            </button>
          </div>
          {payment.cardTipo && (
            <div className="grid grid-cols-3 gap-2.5">
              {["mastercard", "visa", "elo"].map((b) => (
                <button key={b} data-testid={`bandeira-${b}`} onClick={() => setPayment((p) => ({ ...p, bandeira: b }))} className={optCls(payment.bandeira === b)}>
                  {b.charAt(0).toUpperCase() + b.slice(1)}
                </button>
              ))}
            </div>
          )}
          {payment.cardTipo === "credito" && payment.bandeira && (
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Parcelas do saldo</label>
              <select
                data-testid="parcelas-select"
                className={`${inputCls} mt-1`}
                value={payment.parcelas}
                onChange={(e) => setPayment((p) => ({ ...p, parcelas: parseInt(e.target.value) }))}
              >
                {Array.from({ length: 18 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>{n === 1 ? "À vista" : `${n}x`}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">CPF ou CNPJ *</label>
        <input
          data-testid="cpf-cnpj-input"
          inputMode="numeric"
          className={`${inputCls} mt-1`}
          placeholder="000.000.000-00"
          value={payment.cpfCnpj}
          onChange={(e) => setPayment((p) => ({ ...p, cpfCnpj: e.target.value }))}
        />
      </div>

      <div className="flex gap-3">
        <button data-testid="payment-back-btn" onClick={onBack} className="px-5 py-3.5 rounded-xl border-2 border-slate-200 text-navy font-semibold text-sm flex items-center gap-1.5 active:scale-95 transition-transform">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
        <button
          data-testid="ver-contrato-btn"
          onClick={onNext}
          disabled={!canContinue}
          className="flex-1 py-3.5 rounded-xl bg-navy text-[#F5B301] font-heading font-extrabold text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50"
        >
          <FileText className="w-4 h-4" /> VER CONTRATO
        </button>
      </div>
    </div>
  );
}

export function ContractStep({ payload, onBack, onAccept }) {
  const [contract, setContract] = useState(null);
  const [aceite, setAceite] = useState(false);

  useEffect(() => {
    api
      .post("/reservations/preview", payload)
      .then((r) => setContract(r.data.contract))
      .catch((e) => toast.error(e.response?.data?.detail || "Falha ao gerar o contrato"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!contract) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#F5B301]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="contract-step">
      <h2 className="font-heading text-2xl font-bold text-navy">Contrato de prestação de serviço</h2>
      <p className="text-xs text-slate-500">Gerado automaticamente a partir do seu orçamento. Leia com atenção.</p>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 max-h-[46vh] overflow-y-auto space-y-4" data-testid="contract-content">
        {contract.sections.map((sec, i) => (
          <div key={i}>
            <p className="text-xs font-bold tracking-wider text-navy bg-gold-light rounded-lg px-2.5 py-1.5 mb-1.5">{sec.title}</p>
            {sec.lines.map((l, j) => (
              <p key={j} className="text-[13px] text-slate-700 leading-relaxed pl-1">{l}</p>
            ))}
          </div>
        ))}
        <div>
          <p className="text-xs font-bold tracking-wider text-navy bg-gold-light rounded-lg px-2.5 py-1.5 mb-1.5">AVISO SOBRE O HORÁRIO</p>
          <p className="text-[12px] text-slate-600 leading-relaxed pl-1">⚠️ {contract.aviso_horario}</p>
        </div>
        <div>
          <p className="text-xs font-bold tracking-wider text-navy bg-gold-light rounded-lg px-2.5 py-1.5 mb-1.5">POLÍTICA DE CANCELAMENTO</p>
          <p className="text-[12px] text-slate-600 leading-relaxed pl-1">{contract.politica_cancelamento}</p>
        </div>
        <div>
          <p className="text-xs font-bold tracking-wider text-navy bg-gold-light rounded-lg px-2.5 py-1.5 mb-1.5">
            ALTERAÇÕES, ITENS ADICIONAIS E SERVIÇOS NÃO PREVISTOS
          </p>
          <p className="text-[12px] text-slate-600 leading-relaxed pl-1 text-justify" data-testid="contract-clausula">
            {contract.clausula_alteracoes}
          </p>
        </div>
      </div>

      <label className="flex items-start gap-3 bg-white rounded-2xl border-2 border-slate-200 p-4 cursor-pointer" data-testid="aceite-label">
        <input
          data-testid="aceite-checkbox"
          type="checkbox"
          checked={aceite}
          onChange={(e) => setAceite(e.target.checked)}
          className="mt-0.5 w-5 h-5 accent-[#F5B301]"
        />
        <span className="text-xs text-slate-700 leading-relaxed">{contract.aceite_text}</span>
      </label>

      <div className="flex gap-3">
        <button data-testid="contract-back-btn" onClick={onBack} className="px-5 py-3.5 rounded-xl border-2 border-slate-200 text-navy font-semibold text-sm flex items-center gap-1.5 active:scale-95 transition-transform">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
        <button
          data-testid="aceitar-contrato-btn"
          onClick={onAccept}
          disabled={!aceite}
          className="flex-1 py-3.5 rounded-xl bg-navy text-[#F5B301] font-heading font-extrabold text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50"
        >
          <Lock className="w-4 h-4" /> ACEITAR E IR PARA O PIX
        </button>
      </div>
    </div>
  );
}

export function SinalStep({ payload, calc, onBack, onConfirmed }) {
  const [confirming, setConfirming] = useState(false);

  const copyPix = () => {
    navigator.clipboard?.writeText(calc.pix_key || "");
    toast.success("Chave Pix copiada!");
  };

  const confirm = async () => {
    setConfirming(true);
    try {
      const r = await api.post("/reservations", { ...payload, aceite: true });
      onConfirmed(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao registrar a confirmação");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="sinal-step">
      <h2 className="font-heading text-2xl font-bold text-navy">Sinal de reserva — Pix</h2>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Valor total do serviço</span>
          <span className="font-semibold text-navy" data-testid="sinal-total">{brl(calc.total)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Sinal para reserva — {Math.round(calc.sinal_pct || 30)}%</span>
          <span className="font-heading font-extrabold text-navy" data-testid="sinal-valor">{brl(calc.sinal)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Saldo restante</span>
          <span className="font-semibold text-navy" data-testid="sinal-saldo">{brl(calc.saldo)}</span>
        </div>
      </div>

      <div className="bg-gold-light border border-[#F5B301]/60 rounded-2xl p-5 space-y-3" data-testid="pix-area">
        <p className="text-xs font-semibold tracking-wider uppercase text-navy">Chave Pix (CNPJ)</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-white rounded-xl px-4 py-3 text-sm font-bold text-navy" data-testid="pix-key">{calc.pix_key}</code>
          <button data-testid="pix-copy-btn" onClick={copyPix} className="p-3 rounded-xl bg-navy text-[#F5B301] active:scale-90 transition-transform">
            <Copy className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[11px] text-slate-600 leading-relaxed">
          Valor exato do sinal: <strong>{brl(calc.sinal)}</strong>. Faça o Pix para a chave acima (Pix Copia e Cola) e
          depois confirme abaixo.
        </p>
      </div>

      <div className="flex gap-2.5 bg-slate-100 border border-slate-200 rounded-2xl p-3.5">
        <Clock className="w-5 h-5 text-slate-500 shrink-0" />
        <p className="text-[11px] text-slate-600 leading-relaxed">
          Ao clicar em "Já fiz o Pix", sua confirmação será registrada com data e hora e enviada à empresa para
          conferência. <strong>A data só fica oficialmente reservada após a empresa confirmar o recebimento do Pix.</strong>
        </p>
      </div>

      <div className="flex gap-3">
        <button data-testid="sinal-back-btn" onClick={onBack} className="px-5 py-3.5 rounded-xl border-2 border-slate-200 text-navy font-semibold text-sm flex items-center gap-1.5 active:scale-95 transition-transform">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
        <button
          data-testid="ja-fiz-pix-btn"
          onClick={confirm}
          disabled={confirming}
          className="flex-1 py-4 rounded-2xl bg-[#F5B301] text-navy font-heading font-extrabold text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60 shadow-[0_8px_30px_rgba(245,179,1,0.35)]"
        >
          {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
          JÁ FIZ O PIX — CONFIRMAR PAGAMENTO
        </button>
      </div>
    </div>
  );
}

export function FinalStep({ reservation, whatsappLink, onRefresh }) {
  const [refreshing, setRefreshing] = useState(false);
  const dataFmt = reservation.data.split("-").reverse().join("/");
  const confirmado = reservation.pix_status === "confirmado";
  const recusado = reservation.pix_status === "recusado";
  const expirado = reservation.pix_status === "expirado";

  const banner = confirmado
    ? { cls: "bg-emerald-50 border-emerald-300", title: "🟢 PIX CONFIRMADO — RESERVA CONFIRMADA", titleCls: "text-emerald-700",
        sub: "A empresa confirmou o recebimento do seu Pix. Sua data está oficialmente reservada.", subCls: "text-emerald-600" }
    : recusado
    ? { cls: "bg-red-50 border-red-300", title: "🔴 PAGAMENTO NÃO CONFIRMADO — AGENDAMENTO CANCELADO", titleCls: "text-red-700",
        sub: "A empresa não confirmou o recebimento do Pix. O agendamento não foi efetivado e a data foi liberada. Fale conosco pelo WhatsApp.", subCls: "text-red-600" }
    : expirado
    ? { cls: "bg-slate-100 border-slate-300", title: "⚫ RESERVA EXPIRADA", titleCls: "text-slate-600",
        sub: "A confirmação do Pix não ocorreu dentro do prazo e a solicitação expirou. A data voltou a ficar disponível.", subCls: "text-slate-500" }
    : { cls: "bg-amber-50 border-amber-300", title: "🟡 PAGAMENTO INFORMADO — AGUARDANDO CONFIRMAÇÃO", titleCls: "text-amber-700",
        sub: "Recebemos a sua confirmação. A empresa vai conferir o Pix e, após a confirmação, sua data ficará oficialmente reservada.", subCls: "text-amber-600" };

  const dataStatus = confirmado ? "RESERVADA ✅" : recusado ? "CANCELADA ❌" : expirado ? "EXPIRADA" : "PRÉ-RESERVADA ⏳";
  const titulo = confirmado ? "RESERVA CONFIRMADA!" : recusado ? "PAGAMENTO NÃO CONFIRMADO" : expirado ? "RESERVA EXPIRADA" : "PIX INFORMADO!";

  const refresh = async () => {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="reserva-confirmada">
      <div className="text-center">
        {confirmado ? (
          <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-2" />
        ) : recusado ? (
          <AlertTriangle className="w-14 h-14 text-red-500 mx-auto mb-2" />
        ) : (
          <Clock className="w-14 h-14 text-amber-500 mx-auto mb-2" />
        )}
        <h2 className="font-heading text-2xl font-extrabold text-navy">{titulo}</h2>
      </div>

      <div className={`rounded-2xl p-4 border-2 ${banner.cls}`} data-testid="pix-status-banner">
        <p className={`text-sm font-heading font-bold ${banner.titleCls}`}>{banner.title}</p>
        <p className={`text-[11px] mt-1 leading-relaxed ${banner.subCls}`}>{banner.sub}</p>
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 shadow-md p-5 space-y-2.5">
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Número da reserva</span>
          <span className="font-heading font-bold text-navy" data-testid="reserva-numero">{reservation.numero_reserva}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Contrato</span>
          <span className="font-semibold text-navy" data-testid="reserva-contrato">{reservation.numero_contrato}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Data</span>
          <span className={`font-semibold ${confirmado ? "text-emerald-600" : recusado ? "text-red-600" : "text-amber-600"}`} data-testid="reserva-data">
            {dataFmt} · {dataStatus}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Horário</span>
          <span className="font-semibold text-amber-600" data-testid="reserva-horario">{reservation.horario} · SOLICITADO ⏳</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Valor total</span>
          <span className="font-semibold text-navy">{brl(reservation.total)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Sinal (Pix) informado</span>
          <span className="font-semibold text-navy" data-testid="reserva-sinal">{brl(reservation.sinal)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Saldo restante</span>
          <span className="font-semibold text-navy">{brl(reservation.saldo)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Forma de pagamento</span>
          <span className="font-semibold text-navy text-right">{reservation.forma_texto}</span>
        </div>
        {reservation.pix_informado_em && (
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Pagamento informado em</span>
            <span className="font-semibold text-navy" data-testid="reserva-pix-informado">
              {new Date(reservation.pix_informado_em).toLocaleString("pt-BR")}
            </span>
          </div>
        )}
      </div>

      <div className="flex gap-2.5 bg-amber-50 border border-amber-200 rounded-2xl p-3.5" data-testid="reserva-aviso-horario">
        <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
        <p className="text-xs text-amber-700 leading-relaxed">
          IMPORTANTE: o horário escolhido é apenas uma previsão e será confirmado posteriormente pelo profissional
          responsável.
        </p>
      </div>

      <p className="text-[11px] text-slate-500 text-center leading-relaxed">
        Precisa alterar a data? Entre em contato pelo WhatsApp — reagendamentos não são feitos pelo aplicativo.
      </p>

      <button
        data-testid="atualizar-status-btn"
        onClick={refresh}
        disabled={refreshing}
        className="flex items-center justify-center gap-2 w-full py-3 rounded-xl border-2 border-slate-200 text-navy font-semibold text-sm active:scale-95 transition-transform disabled:opacity-60"
      >
        <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
        ATUALIZAR STATUS
      </button>

      <a
        data-testid="visualizar-contrato-btn"
        href={`${API}/reservations/${reservation.reservation_id}/pdf`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full py-4 rounded-2xl bg-navy text-[#F5B301] font-heading font-extrabold text-sm active:scale-95 transition-transform"
      >
        <FileText className="w-5 h-5" /> VISUALIZAR CONTRATO
      </a>
      <a
        data-testid="reserva-whatsapp-btn"
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full py-4 rounded-2xl bg-[#25D366] text-white font-heading font-extrabold text-sm active:scale-95 transition-transform"
      >
        <MessageCircle className="w-5 h-5" /> ENVIAR COMPROVANTE NO WHATSAPP
      </a>
    </div>
  );
}
